import React, { useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import TopNavigation from '@cloudscape-design/components/top-navigation';
import SideNavigation from '@cloudscape-design/components/side-navigation';
import Tabs from '@cloudscape-design/components/tabs';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { AppArea } from '../services/types';

/** Tab identifiers for the primary navigation. */
export type AppTab = 'asn' | 'promised' | 'leadtime' | 'members';

const NAV_ITEMS: { id: AppTab; label: string }[] = [
    { id: 'asn', label: 'ASN' },
    { id: 'promised', label: 'Promised Date' },
    { id: 'leadtime', label: 'Lead Time' },
    { id: 'members', label: 'Supplier Users' },
];

/** Presentation for each top-level area in the navigation drawer. */
const AREA_META: Record<AppArea, { label: string; description: string }> = {
    connect: {
        label: 'Supplier Gateway',
        description: 'Submit ASNs, promised dates, and lead times',
    },
    control: {
        label: 'Supplier Control',
        description: 'Manage supplier access and approvals',
    },
    insights: {
        label: 'Supplier Insights',
        description: 'Performance reporting across suppliers',
    },
};

interface AppShellProps {
    children: React.ReactNode;
    userEmail?: string;
    onSignOut?: () => void;
    /** Optional actions rendered above the content (e.g. integration guide). */
    navActions?: React.ReactNode;
    /** Active primary-nav tab. When provided, the tab row is shown. */
    tab?: AppTab;
    onTabChange?: (tab: AppTab) => void;
    /**
     * Which primary-nav tabs to show. Defaults to all. Lets the caller hide
     * role-gated tabs (e.g. "Supplier Users" only for a supplier admin).
     */
    visibleTabs?: AppTab[];
    /** Areas the user can access. A left nav appears when > 1. */
    areas?: AppArea[];
    activeArea?: AppArea;
    onAreaChange?: (area: AppArea) => void;
    /** When provided, clicking the brand/title returns to the home view. */
    onHome?: () => void;
    /** Content for the right slide-over panel (AppLayout tools), e.g. the guide. */
    tools?: React.ReactNode;
    /** Whether the right slide-over is open (controlled). */
    toolsOpen?: boolean;
    onToolsChange?: (open: boolean) => void;
}

/**
 * Branded application shell built on Cloudscape AppLayout: a TopNavigation bar
 * (brand + user identity + sign out), an optional left SideNavigation for
 * multi-area users, a primary-nav tab row, the page content, and an optional
 * right slide-over panel (tools) for things like the integration guide.
 */
export default function AppShell({
    children,
    userEmail,
    onSignOut,
    navActions,
    tab,
    onTabChange,
    visibleTabs,
    areas,
    activeArea,
    onAreaChange,
    onHome,
    tools,
    toolsOpen,
    onToolsChange,
}: AppShellProps) {
    const multiArea = (areas?.length ?? 0) > 1;
    // Open the workspace rail by default for multi-area users so it's visible
    // on entry; the hamburger (rendered by AppLayout) toggles it from there.
    const [navOpen, setNavOpen] = useState(multiArea);

    return (
        <>
            <div id="top-nav">
                <TopNavigation
                    identity={{
                        href: '#',
                        // Product name as the brand (what the user logs into).
                        // The org ("Tech Supply Chain") is shown as the user
                        // menu's description so both are visible, without a
                        // confusing "org · product" concatenation.
                        title: 'Supplier Gateway',
                        onFollow: (e) => {
                            e.preventDefault();
                            onHome?.();
                        },
                    }}
                    utilities={
                        userEmail && onSignOut
                            ? [
                                  {
                                      type: 'menu-dropdown',
                                      text: userEmail,
                                      description: 'Tech Supply Chain',
                                      iconName: 'user-profile',
                                      items: [{ id: 'signout', text: 'Sign out' }],
                                      onItemClick: ({ detail }) => {
                                          if (detail.id === 'signout') onSignOut();
                                      },
                                  },
                              ]
                            : []
                    }
                />
            </div>
            <AppLayout
                headerSelector="#top-nav"
                toolsHide={!tools}
                tools={tools}
                toolsOpen={tools ? toolsOpen : undefined}
                toolsWidth={520}
                onToolsChange={({ detail }) => onToolsChange?.(detail.open)}
                navigationHide={!multiArea}
                navigationOpen={navOpen}
                onNavigationChange={({ detail }) => setNavOpen(detail.open)}
                navigation={
                    <SideNavigation
                        header={{ text: 'Workspaces', href: `#${activeArea ?? ''}` }}
                        activeHref={`#${activeArea ?? ''}`}
                        onFollow={(e) => {
                            e.preventDefault();
                            const next = e.detail.href.replace(/^#/, '');
                            if (next && next !== activeArea) {
                                onAreaChange?.(next as AppArea);
                            }
                        }}
                        items={(areas ?? []).map((a) => ({
                            type: 'link',
                            text: AREA_META[a].label,
                            href: `#${a}`,
                        }))}
                    />
                }
                content={
                    <SpaceBetween size="l">
                        {navActions ? <Box textAlign="right">{navActions}</Box> : null}
                        {tab ? (
                            <Tabs
                                activeTabId={tab}
                                onChange={({ detail }) =>
                                    onTabChange?.(detail.activeTabId as AppTab)
                                }
                                tabs={NAV_ITEMS.filter(
                                    (item) => !visibleTabs || visibleTabs.includes(item.id),
                                ).map((item) => ({
                                    id: item.id,
                                    label: item.label,
                                    content: item.id === tab ? children : undefined,
                                }))}
                            />
                        ) : (
                            children
                        )}
                    </SpaceBetween>
                }
            />
        </>
    );
}

AppShell.defaultProps = {
    userEmail: undefined,
    onSignOut: undefined,
    navActions: undefined,
    tab: undefined,
    onTabChange: undefined,
    visibleTabs: undefined,
    areas: undefined,
    activeArea: undefined,
    onAreaChange: undefined,
    onHome: undefined,
    tools: undefined,
    toolsOpen: undefined,
    onToolsChange: undefined,
};
